import React from "react";
import { motion } from "framer-motion";
import { CheckCircle, ArrowUpRight } from "@phosphor-icons/react";
import PageHero from "../components/PageHero";
import { Section, SectionHeading, FadeIn } from "../components/Section";
import { STATS, VALUES } from "../data/content";
import Button from "../components/Button";

const About = () => {
  return (
    <>
      <PageHero
        overline="About Us"
        title="Industry expertise. Strategic alliances. Lasting impact."
        description="Fradro Group is built on technical depth, visionary leadership and durable partnerships across Nigeria's energy ecosystem."
        image="https://images.pexels.com/photos/7794060/pexels-photo-7794060.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
        testid="about-hero"
      />

      <Section spacing="py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <FadeIn>
              <img
                src="https://images.pexels.com/photos/5884386/pexels-photo-5884386.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
                alt="Energy infrastructure"
                className="w-full h-[480px] object-cover border border-border"
                loading="lazy"
              />
            </FadeIn>
          </div>
          <div className="lg:col-span-7">
            <FadeIn delay={0.1}>
              <div className="overline text-primary mb-4">— Our story</div>
              <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl leading-[1.05]">
                Welcome to Fradro Group.
              </h2>
              <div className="mt-6 space-y-5 text-muted-foreground leading-relaxed">
                <p>
                  Fradro Group is a diversified energy conglomerate committed
                  to delivering innovative, efficient and sustainable energy
                  solutions across Nigeria and beyond. Our subsidiaries —
                  <strong className="text-foreground"> Fradro International Limited</strong> and{" "}
                  <strong className="text-foreground">Fradro Gas &amp; Power Limited</strong> — provide a full
                  spectrum of services from petroleum trading and refined
                  product distribution to natural gas, power generation and
                  upstream support.
                </p>
                <p>
                  Together, these divisions deliver integrated energy
                  solutions: cost-effective bunkering, refined petroleum
                  products and state-of-the-art natural gas infrastructure.
                  Our commitment to innovation, safety and community
                  development has made us a trusted partner in Nigeria's
                  evolving energy landscape.
                </p>
              </div>

              <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                {[
                  "World-class technical expertise",
                  "Visionary leadership team",
                  "Trusted global partnerships",
                  "Integrated energy solutions",
                  "Decades of field experience",
                  "Safety-first operations",
                ].map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-2 text-sm text-foreground/85"
                  >
                    <CheckCircle
                      weight="fill"
                      size={18}
                      className="text-primary mt-0.5 shrink-0"
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Stats */}
      <Section
        className="bg-card border-y border-border"
        spacing="py-20 md:py-28"
      >
        <SectionHeading
          overline="Our performance"
          title="Numbers that mark our footprint."
        />
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 border-t border-border">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className={`p-8 md:p-10 border-b border-border ${
                i % 2 !== 0 ? "border-l" : ""
              } lg:border-l ${i === 0 ? "lg:border-l-0" : ""}`}
            >
              <div className="font-display font-extrabold text-5xl md:text-6xl tracking-tight text-primary">
                {s.value}
              </div>
              <div className="overline text-muted-foreground mt-3">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Values */}
      <Section spacing="py-20 md:py-28">
        <SectionHeading
          overline="Core values"
          title="The principles that guide every project we touch."
        />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="p-8 border border-border bg-card hover:border-primary transition-all hover:-translate-y-1 group"
            >
              <div className="font-mono text-xs text-primary mb-4">
                0{i + 1}
              </div>
              <h3 className="font-display font-bold text-xl mb-3 group-hover:text-primary transition-colors">
                {v.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {v.description}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 text-center">
          <Button to="/contact" iconRight={ArrowUpRight} size="lg">
            Partner with us
          </Button>
        </div>
      </Section>
    </>
  );
};

export default About;
